import { useState, useEffect } from "react";
import { X, Calendar, Save, MapPin, Users } from "lucide-react";
import {
  IOrganizationEvent,
  ICreateOrganizationEventPayload,
} from "@/types/EventTypes";
import RichText from "@/components/ui/form/RichText";
import DropzoneFile from "@/components/ui/form/DropzoneFile";
import { V1BEClient } from "@/clients";

interface OrganizationEventFormProps {
  event?: IOrganizationEvent | null;
  onSave: (eventData: ICreateOrganizationEventPayload) => void;
  onClose: () => void;
}

const OrganizationEventForm = ({
  event,
  onSave,
  onClose,
}: OrganizationEventFormProps) => {
  const [formData, setFormData] = useState<ICreateOrganizationEventPayload>({
    organization_id: import.meta.env.VITE_ORGANIZATION_ID || "",
    name: "",
    description: "",
    date_from: "",
    date_to: "",
  });
  const [errors, setErrors] = useState<
    Partial<ICreateOrganizationEventPayload>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);

  useEffect(() => {
    if (event) {
      setFormData({
        id: event.id, // Include the ID for updates
        organization_id: event.organization_id,
        name: event.name,
        description: event.description || "",
        date_from: event.date_from || "",
        date_to: event.date_to || "",
        image_url: event.image_url,
        organization_event_string_properties:
          event.organization_event_string_properties?.map((p) => ({
            id: p.id,
            name: p.name,
            value: p.value,
          })),
        organization_event_int_properties:
          event.organization_event_int_properties?.map((p) => ({
            id: p.id,
            name: p.name,
            value: p.value,
          })),
      });

      if (event.organization_event_string_properties) {
        const locationProp = event.organization_event_string_properties.find(
          (p) => p.name === "location"
        );
        if (locationProp) {
          handleLocationChange(locationProp.value);
        }
      }
    } else {
      setFormData({
        organization_id: import.meta.env.VITE_ORGANIZATION_ID || "",
        name: "",
        description: "",
        date_from: "",
        date_to: "",
      });
    }
    setErrors({});
    setImageFiles([]);
  }, [event]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleLocationChange = (value: string) => {
    setFormData((prev) => {
      const existingProps = prev.organization_event_string_properties || [];
      const locationPropIndex = existingProps.findIndex(
        (p) => p.name === "location"
      );

      let newProps;
      if (locationPropIndex >= 0) {
        newProps = [...existingProps];
        newProps[locationPropIndex] = { ...newProps[locationPropIndex], value };
      } else {
        newProps = [...existingProps, { name: "location", value, id: null }];
      }

      return { ...prev, organization_event_string_properties: newProps };
    });
  };

  const handleIntPropertyChange = (name: string, value: string) => {
    const intValue = value === "" ? 0 : parseInt(value);
    setFormData((prev) => {
      const existingProps = prev.organization_event_int_properties || [];
      const propIndex = existingProps.findIndex((p) => p.name === name);

      let newProps;
      if (propIndex >= 0) {
        newProps = [...existingProps];
        newProps[propIndex] = { ...newProps[propIndex], value: intValue };
      } else {
        newProps = [...existingProps, { name, value: intValue, id: null }];
      }

      return { ...prev, organization_event_int_properties: newProps };
    });
  };


  const validateForm = () => {
    const newErrors: Partial<ICreateOrganizationEventPayload> = {};

    if (!formData.organization_id.trim()) {
      newErrors.organization_id = "Organization ID is required";
    }

    if (!formData.name.trim()) {
      newErrors.name = "Event name is required";
    }

    // Description is optional, so no validation needed

    if (formData.date_from && formData.date_to) {
      const startDate = new Date(formData.date_from);
      const endDate = new Date(formData.date_to);

      if (endDate < startDate) {
        newErrors.date_to = "End date must be after or equal to start date";
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      let imageUrl = formData.image_url;

      // Upload image if a new file is selected
      if (imageFiles.length > 0) {
        const file = imageFiles[0];
        if (file) {
          const uploadedFile = await V1BEClient.uploadFile(file);
          if (uploadedFile) {
            imageUrl = uploadedFile.file_url;
          }
        }
      }

      onSave({
        ...formData,
        image_url: imageUrl,
      });
    } catch (error) {
      console.error("Error saving event:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {event ? "Edit Event" : "Create New Event"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Organization ID - Hidden field */}
          <input
            type="hidden"
            id="organization_id"
            value={formData.organization_id}
          />
          {/* Event ID - Hidden field (for updates) */}
          {formData.id && <input type="hidden" id="id" value={formData.id} />}

          {/* Event Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Event Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                errors.name ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Enter event name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Event Description */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Event Description
            </label>
            <RichText
              value={formData.description || ""}
              onChange={(value) => handleInputChange("description", value)}
              placeholder="Enter event description (optional)"
              className={errors.description ? "border-red-300" : ""}
              minHeight={150}
              maxHeight={300}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="date_from"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Start Date
              </label>
              <input
                type="date"
                id="date_from"
                value={formData.date_from}
                onChange={(e) => handleInputChange("date_from", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                  errors.date_from ? "border-red-300" : "border-gray-300"
                }`}
              />
              {errors.date_from && (
                <p className="mt-1 text-sm text-red-600">{errors.date_from}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="date_to"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                End Date
              </label>
              <input
                type="date"
                id="date_to"
                value={formData.date_to}
                onChange={(e) => handleInputChange("date_to", e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                  errors.date_to ? "border-red-300" : "border-gray-300"
                }`}
              />
              {errors.date_to && (
                <p className="mt-1 text-sm text-red-600">{errors.date_to}</p>
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <label
              htmlFor="location"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Location
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="location"
                value={
                  formData.organization_event_string_properties?.find(
                    (p) => p.name === "location"
                  )?.value || ""
                }
                onChange={(e) => handleLocationChange(e.target.value)}
                className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                placeholder="Enter event location"
              />
            </div>
          </div>

          {/* Expected Attendance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="expected_supplier_attendance"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Expected Supplier Attendance
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="expected_supplier_attendance"
                  value={
                    formData.organization_event_int_properties?.find(
                      (p) => p.name === "expected_supplier_attendance"
                    )?.value || ""
                  }
                  onChange={(e) =>
                    handleIntPropertyChange(
                      "expected_supplier_attendance",
                      e.target.value
                    )
                  }
                  className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="expected_guest_attendance"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Expected Guest Attendance
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  id="expected_guest_attendance"
                  value={
                    formData.organization_event_int_properties?.find(
                      (p) => p.name === "expected_guest_attendance"
                    )?.value || ""
                  }
                  onChange={(e) =>
                    handleIntPropertyChange(
                      "expected_guest_attendance",
                      e.target.value
                    )
                  }
                  className="w-full pl-10 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Event Image
            </label>
            {formData.image_url ? (
              <div className="relative w-full h-full bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                <img
                  src={formData.image_url}
                  alt="Event"
                  className="w-full h-full"
                />
                <button
                  type="button"
                  onClick={async () => {
                    if (formData.image_url) {
                      await V1BEClient.deleteFile(formData.image_url);

                      if (formData.id) {
                        await V1BEClient.updateOrganizationEvent({
                          ...formData,
                          image_url: "",
                        });
                      }

                      setFormData((prev) => ({ ...prev, image_url: "" }));
                    }
                  }}
                  className="absolute top-2 right-2 bg-white text-gray-500 p-1 rounded-full hover:text-red-500 shadow-sm border border-gray-200 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <DropzoneFile
                files={imageFiles}
                setFiles={setImageFiles}
                maxFiles={1}
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {event ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {event ? "Update Event" : "Create Event"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationEventForm;
